import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { apiFetch } from "../../../app/apiClient";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { decreaseFullScreenLoading, increaseFullScreenLoading } from "../../../appSlice";
import ComponentLoading from "../../../components/ComponentLoading";
import { currentToken, isDomainAdmin, sessionRehydrated, userLoggedIn } from "../../login/loginSlice";
import "./DomainSettings.scss";

function DomainSettings() {
    const dispatch = useAppDispatch();
    const userToken = useAppSelector(currentToken);
    const [displayName, setDisplayName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(<></>);
    const [saveMessage, setSaveMessage] = useState(<></>);

    const rehydrated = useAppSelector(sessionRehydrated);
    const loggedIn = useAppSelector(userLoggedIn);
    const userIsDomainAdmin = useAppSelector(isDomainAdmin);

    useEffect(() => {
        if (!rehydrated || !loggedIn || !userIsDomainAdmin) {
            return;
        }
        const abortController = new AbortController();
        async function loadDomainSettings() {
            setIsLoading(true);
            setLoadError(<></>);
            try {
                const response = await apiFetch("/api/domain/settings", {
                    method: "GET",
                    token: userToken,
                    signal: abortController.signal
                });
                if (!response.ok) {
                    throw new Error("LOAD_FAILED");
                }
                const data = await response.json();
                setDisplayName(data.displayName ?? "");
            } catch (error: any) {
                if (error.name !== "AbortError" && error.name !== "SessionExpiredError") {
                    setLoadError(<span className={"validation"}>Failed to load domain settings.</span>);
                }
            } finally {
                setIsLoading(false);
            }
        }
        loadDomainSettings();
        return () => abortController.abort();
    }, [rehydrated, loggedIn, userIsDomainAdmin, userToken]);

    const saveDomainSettings = async () => {
        dispatch(increaseFullScreenLoading());
        setSaveMessage(<></>);
        try {
            const response = await apiFetch("/api/domain/settings", {
                method: "PUT",
                token: userToken,
                body: JSON.stringify({ displayName })
            });
            if (!response.ok) {
                throw new Error("SAVE_FAILED");
            }
            setSaveMessage(<span className={"confirmation"}>Domain settings saved.</span>);
        } catch (error: any) {
            if (error.name !== "SessionExpiredError") {
                setSaveMessage(<span className={"validation"}>Failed to save domain settings. Please try again.</span>);
            }
        }
        dispatch(decreaseFullScreenLoading());
    };

    if (!rehydrated) {
        return null;
    }
    if (!loggedIn) {
        return <Navigate to="/login" />;
    }
    if (!userIsDomainAdmin) {
        return <Navigate to="/inbox" />;
    }

    return (<div id={"domainSettingsContent"} className={"content"}>
        <ComponentLoading isActive={isLoading} />
        <h1>Domain Settings</h1>
        {loadError}
        <label>
            Domain display name:
            <input
                type="text"
                placeholder={"Your Company Name"}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onKeyDown={(k) => { if (k.key === "Enter") { saveDomainSettings(); } }}
            />
            <small>This is the name shown to your users across the app, e.g. in branding and email headers.</small>
        </label>
        {saveMessage}
        <button className={"genericBtn"} onClick={saveDomainSettings}>Save</button>
    </div>);
}

export default DomainSettings;
